import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "@/components/ui/button";
import { Crop as CropIcon, RotateCcw, X, Loader2 } from "lucide-react";

const TARGET_W = 1600;
const TARGET_H = 838;
const ASPECT = TARGET_W / TARGET_H;

interface InlineImageCropProps {
  imageSrc: string;
  onCropDone: (croppedFile: File) => void;
  onCancel: () => void;
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number) {
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, ASPECT, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}

export function InlineImageCrop({ imageSrc, onCropDone, onCancel }: InlineImageCropProps) {
  const { t } = useTranslation('producerEventForm');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const [saving, setSaving] = useState(false);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const c = centerAspectCrop(naturalWidth, naturalHeight);
    setCrop(c);
    setCompletedCrop(c);
  }, []);

  const handleReset = () => {
    if (imgRef.current) {
      const c = centerAspectCrop(imgRef.current.naturalWidth, imgRef.current.naturalHeight);
      setCrop(c);
      setCompletedCrop(c);
    }
  };

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop) return;
    setSaving(true);

    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: (completedCrop.x ?? 0) * scaleX,
      y: (completedCrop.y ?? 0) * scaleY,
      width: (completedCrop.width ?? 0) * scaleX,
      height: (completedCrop.height ?? 0) * scaleY,
    };

    const canvas = document.createElement("canvas");
    canvas.width = TARGET_W;
    canvas.height = TARGET_H;
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, TARGET_W, TARGET_H);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], "cover-cropped.jpg", { type: "image/jpeg" });
          onCropDone(file);
        }
        setSaving(false);
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <div className="space-y-4 rounded-2xl border border-black/10 dark:border-white/10 p-5 bg-background shadow-xl shadow-black/5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold tracking-tight text-foreground flex items-center gap-2">
            <CropIcon className="h-4 w-4 stroke-[1.75] text-foreground/80" />
            {t('imageCrop.inline.titulo')}
          </h4>
          <p className="text-xs text-muted-foreground font-normal leading-relaxed mt-0.5">
            {t('imageCrop.inline.dimensaoFinal.label')} <span className="font-mono font-medium text-foreground">{t('imageCrop.inline.dimensaoFinal.valor', { width: TARGET_W, height: TARGET_H })}</span>
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onCancel} className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="relative rounded-xl overflow-hidden bg-zinc-100/70 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-zinc-800/80 p-3 flex items-center justify-center">
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={ASPECT}
          minWidth={100}
          className="max-w-full rounded-lg overflow-hidden"
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt={t('imageCrop.inline.altRecortar')}
            onLoad={onImageLoad}
            className="max-h-[45vh] w-full object-contain select-none"
            crossOrigin="anonymous"
          />
        </ReactCrop>
      </div>

      <div className="flex items-center justify-between pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          disabled={saving}
          className="rounded-full text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors h-9 px-3.5 gap-1.5"
        >
          <RotateCcw className="h-3.5 w-3.5 stroke-[1.75]" /> {t('imageCrop.inline.resetar')}
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full text-xs font-medium h-9 px-4 text-muted-foreground hover:text-foreground hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            {t('imageCrop.inline.cancelar')}
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={saving}
            className="rounded-full text-xs font-medium h-9 px-5 bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-none gap-1.5"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CropIcon className="h-3.5 w-3.5 stroke-[1.75]" />}
            {saving ? t('imageCrop.inline.processando') : t('imageCrop.inline.confirmar')}
          </Button>
        </div>
      </div>
    </div>
  );
}
